import { DatePipe, NgClass } from '@angular/common';
import {
  Component,
  computed,
  inject,
  input,
  OnDestroy,
  OnInit,
  output,
  signal,
  TemplateRef,
  viewChild
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal, NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import {
  AbilityService,
  BtnLoaderDirective,
  DocPreviewComponent,
  DropdownData,
  GenericDropdownComponent,
  NgbModalService,
  SvgIconsComponent
} from '@zayan-erp/shared';
import { environment } from 'apps/cinari-erp-client/src/environments/environment';
import { ToastrService } from 'ngx-toastr';
import { merge } from 'rxjs';
import {
  ApsDealDocument,
  DealDocumentHelper,
  DealDocumentSignature,
  DealDocumentSignatureFlowType,
  DealDocumentsService,
  EnvelopeStatus,
  IVoidContractReason,
  MAX_AUTO_RESENDS,
  RecipientStatus,
  SalesApsService,
  SignatureCompletionSource,
  VoidRequestedPayload
} from '@zayan-erp/sales';

export type DocSigningContext = 'aps' | 'misc';

@Component({
  selector: 'lib-signable-document-card',
  standalone: true,
  imports: [
    DocPreviewComponent,
    SvgIconsComponent,
    DatePipe,
    GenericDropdownComponent,
    NgClass,
    BtnLoaderDirective,
    NgbTooltip
  ],
  templateUrl: './signable-document-card.html',
  styleUrl: './signable-document-card.scss'
})
export class SignableDocumentCard implements OnInit, OnDestroy {
  readonly #route = inject(ActivatedRoute);
  readonly #router = inject(Router);
  readonly #modalService = inject(NgbModal);
  readonly #ngbModalService = inject(NgbModalService);
  readonly #salesApsService = inject(SalesApsService);
  readonly #toastrService = inject(ToastrService);
  readonly #dealDocService = inject(DealDocumentsService);
  readonly #abilityService = inject(AbilityService);

  readonly canWriteDeals = computed(() => this.#abilityService.ability.can('write-sales-deals', 'deals'));
  readonly currentLotNo = computed(() => this.#salesApsService.currentDeal()?.lot?.lotNo ?? null);

  documentPreviewModal = viewChild<TemplateRef<unknown>>('documentPreviewModal');
  voidContractReasonModal = viewChild<TemplateRef<unknown>>('voidContractReasonModal');
  regenerateContractConfirmationModal = viewChild<TemplateRef<unknown>>('regenerateContractConfirmationModal');
  sendToExecutiveConfirmationModal = viewChild<TemplateRef<unknown>>('sendToExecutiveConfirmationModal');
  resendConfirmationModal = viewChild<TemplateRef<unknown>>('resendConfirmationModal');
  resolveSignerConfirmationModal = viewChild<TemplateRef<unknown>>('resolveSignerConfirmationModal');

  readonly DealDocumentSignatureFlowType = DealDocumentSignatureFlowType;
  readonly EnvelopeStatus = EnvelopeStatus;
  readonly RecipientStatus = RecipientStatus;
  readonly maxAutoResends = MAX_AUTO_RESENDS;

  refreshRequested = output<void>();

  signingContext = input<DocSigningContext>('misc');
  documents = input.required<ApsDealDocument[]>();
  // deletable = input<boolean | undefined>(undefined);
  enableEmailSignatureFlow = input<boolean | undefined>(undefined);
  enableInPersonSignatureFlow = input<boolean | undefined>(undefined);
  origin = input<string | undefined>(undefined);
  allowExecutiveSigning = input<boolean>(false);

  handleSigningReturn = input<boolean>(true);

  showVoidAndRegenerate = computed(() => !this.allowExecutiveSigning());
  showEmailFlow = computed(() => (this.enableEmailSignatureFlow() ?? true) && this.canWriteDeals());
  showInPersonFlow = computed(() => (this.enableInPersonSignatureFlow() ?? true) && this.canWriteDeals());

  effectiveOrigin = computed(() => this.origin() ?? (this.signingContext() === 'misc' ? 'misc' : 'aps'));

  activeDocument = signal<ApsDealDocument | null>(null);
  activeSignature = signal<DealDocumentSignature | null>(null);
  selectedPreview = signal<{ name: string; url: string } | null>(null);
  showVoidReasonError = signal(false);
  selectedVoidReasonId = signal<string>('');
  voidComment = signal<string>('');
  voidReasons = signal<IVoidContractReason[]>([]);
  voidContractCheck = signal(false);

  voidReasonsDropdownItems = computed<DropdownData[]>(() =>
    this.voidReasons().map(r => ({
      id: r.id,
      firstLabel: r.description
    }))
  );

  selectedVoidReason = computed<IVoidContractReason | null>(
    () => this.voidReasons().find(r => r.id === this.selectedVoidReasonId()) ?? null
  );

  voidReasonRequiresComment = computed(() => this.selectedVoidReason()?.requiresComment ?? false);

  #refreshTimeoutId: ReturnType<typeof setTimeout> | null = null;

  ngOnInit(): void {
    if (this.showVoidAndRegenerate()) {
      this.#dealDocService.getContractVoidReason().subscribe({
        next: reasons => this.voidReasons.set(reasons ?? [])
      });
    }

    if (this.handleSigningReturn()) {
      const events = this.#route.snapshot.queryParamMap.getAll('event');
      const event = events[events.length - 1] ?? null;

      if (event) {
        this.removeSigningReturnQueryParamsFromUrl();
      }

      if (event === 'signing_complete') {
        this.#toastrService.success(
          'Document has been signed. The status might take some time to reflect document signature status on this page',
          'Success',
          { timeOut: 20000, closeButton: true }
        );
        this.handleSigningCompleteReturn();
      }
    }

    this.scheduleAutoRefresh();
  }

  ngOnDestroy(): void {
    this.clearAutoRefreshTimeout();
  }

  isExpired(document: ApsDealDocument): boolean {
    return document.status === EnvelopeStatus.Expired;
  }

  autoResendExhausted(document: ApsDealDocument): boolean {
    return (document.autoResendCount ?? 0) >= this.maxAutoResends;
  }

  autoResendRemaining(document: ApsDealDocument): number {
    return Math.max(0, this.maxAutoResends - (document.autoResendCount ?? 0));
  }

  signedCount(document: ApsDealDocument): number {
    return document.dealDocumentSignatures.filter(sig => sig.status === RecipientStatus.Complete).length;
  }

  totalSigners(document: ApsDealDocument): number {
    return document.dealDocumentSignatures.length;
  }

  showSignerSection(document: ApsDealDocument): boolean {
    return document.dealDocumentSignatures.length > 0;
  }

  buyersSigned(document: ApsDealDocument): boolean {
    const nonExecutiveSigners = document.dealDocumentSignatures.filter(sig => !this.isExecutiveSigner(sig));
    return (
      nonExecutiveSigners.length > 0 &&
      nonExecutiveSigners.every(sig => DealDocumentHelper.isRecipientSigningComplete(sig.status))
    );
  }

  canSendToExecutive(document: ApsDealDocument, signature: DealDocumentSignature): boolean {
    if (!this.isExecutiveSigner(signature)) {
      return false;
    }

    if (document.status === EnvelopeStatus.Complete || DealDocumentHelper.isExecutiveReleasedStatus(signature.status)) {
      return false;
    }

    return (
      signature.status === RecipientStatus.Pending &&
      (document.signingType === DealDocumentSignatureFlowType['DocuSign'] ||
        document.signingType === DealDocumentSignatureFlowType['DocuSignInPerson']) &&
      this.buyersSigned(document)
    );
  }

  showSendToExecutiveButton(document: ApsDealDocument, signature: DealDocumentSignature): boolean {
    return (
      !this.allowExecutiveSigning() &&
      !this.isCoopDocument(document) &&
      this.canSendToExecutive(document, signature) &&
      this.canWriteDeals()
    );
  }

  /** Executives no longer sign co-ops; an old co-op's executive row gets no executive actions. */
  isCoopDocument(document: ApsDealDocument): boolean {
    return DealDocumentHelper.isCoopDealDocument(document);
  }

  copyRecipientsStatusText(document: ApsDealDocument): string {
    return document.status === EnvelopeStatus.Complete
      ? 'Copy sent by DocuSign when the realtor signed'
      : 'Gets a copy when the realtor signs';
  }

  canResolveSignerInDocuSign(document: ApsDealDocument, signature: DealDocumentSignature): boolean {
    if (this.signingContext() !== 'aps') {
      return false;
    }

    if (!this.showVoidAndRegenerate()) {
      return false;
    }

    if (this.isProspectSigner(signature)) {
      return false;
    }

    if (!this.isCurrentActiveSigner(document, signature)) {
      return false;
    }

    if (
      document.signingType !== DealDocumentSignatureFlowType['DocuSign'] &&
      document.signingType !== DealDocumentSignatureFlowType['DocuSignInPerson']
    ) {
      return false;
    }

    if (
      document.status === EnvelopeStatus.Complete ||
      document.status === EnvelopeStatus.Voided ||
      document.status === EnvelopeStatus.Declined ||
      !document.envelopeId
    ) {
      return false;
    }

    return !DealDocumentHelper.isRecipientSigningComplete(signature.status);
  }

  completionSourceLabel(signature: DealDocumentSignature): string {
    return DealDocumentHelper.completionSourceLabel(signature.completionSource);
  }

  resolveSignerModalBody(): string {
    const name = this.activeSignature()?.signee?.name?.trim() || 'This signer';
    return `${name}\'s copy will be converted to a Carbon Copy in DocuSign and marked complete in the portal. They will no longer need to sign. Continue?`;
  }

  // isDeletable(document: ApsDealDocument): boolean {
  //   return (this.deletable() ?? false) || document.documentType === 'CUSTOM' || document.documentType === 'AMENDMENT';
  // }

  getDocumentUrl(document: ApsDealDocument): string {
    const hasSigned = document.dealDocumentSignatures.some(sig => sig.status === RecipientStatus.Complete);
    if (hasSigned) {
      return document.signedS3Document?.directUrl ?? '';
    }

    if (this.showVoidAndRegenerate()) {
      return document.pdfS3Document?.directUrl ?? document.s3Document?.directUrl ?? '';
    }

    return document.s3Document?.directUrl ?? '';
  }

  formatSigneeType(signature: DealDocumentSignature): string {
    return this.#salesApsService.formatSigneeType(signature.entityType);
  }

  isExecutiveSigner(signature: DealDocumentSignature): boolean {
    return signature.entityType?.toLowerCase() === 'user';
  }

  isProspectSigner(signature: DealDocumentSignature): boolean {
    return signature.entityType?.toLowerCase() === 'prospect';
  }

  isCurrentActiveSigner(document: ApsDealDocument, signature: DealDocumentSignature): boolean {
    if (signature.sequence == null) {
      return true;
    }

    return !document.dealDocumentSignatures.some(
      other =>
        other.id !== signature.id &&
        other.sequence != null &&
        other.sequence < signature.sequence! &&
        !DealDocumentHelper.isRecipientSigningComplete(other.status)
    );
  }

  isFailedSigner(signature: DealDocumentSignature): boolean {
    return (
      signature.status === RecipientStatus.AutoResponded || signature.status === RecipientStatus.AuthenticationFailed
    );
  }

  openPreviewModal(document: ApsDealDocument): void {
    const url = this.getDocumentUrl(document);
    if (!url) {
      return;
    }

    this.selectedPreview.set({ name: document.documentType, url });
    const modalRef = this.#modalService.open(this.documentPreviewModal(), this.#ngbModalService.fullscreenModal);
    merge(modalRef.closed, modalRef.dismissed).subscribe(() => this.selectedPreview.set(null));
  }

  sendViaEmailButtonId(documentId: string): string {
    return `send-via-email-${documentId}`;
  }

  signNowButtonId(documentId: string): string {
    return `sign-now-${documentId}`;
  }

  voidContractButtonId(documentId: string): string {
    return `void-contract-${documentId}`;
  }

  sendToExecutiveButtonId(documentId: string): string {
    return `send-to-executive-${documentId}`;
  }

  initiateSignatureProcess(document: ApsDealDocument, flowType: DealDocumentSignatureFlowType): void {
    if (!this.canWriteDeals()) return;
    const buttonId =
      flowType === DealDocumentSignatureFlowType.DocuSignInPerson
        ? this.signNowButtonId(document.id)
        : this.sendViaEmailButtonId(document.id);

    this.#dealDocService
      .initiateDealInPersonSignature(document.id, flowType, buttonId, document.dealId || undefined)
      .subscribe({
        next: () => {
          this.onCardRefreshRequested();
          this.#salesApsService.realoadDeal.next(null);
        }
      });
  }

  openResendConfirmationModal(document: ApsDealDocument): void {
    if (!this.canWriteDeals()) return;
    this.activeDocument.set(document);
    this.#modalService.open(this.resendConfirmationModal(), this.#ngbModalService.confirmationModal).result.then(
      () => this.resend(),
      () => undefined
    );
  }

  resend(): void {
    if (!this.canWriteDeals()) return;
    const document = this.activeDocument();
    if (!document) {
      return;
    }

    this.#dealDocService.resendDealDocument(document.id, document.dealId).subscribe({
      next: () => {
        this.onCardRefreshRequested();
        this.#salesApsService.realoadDeal.next(null);
      }
    });
  }

  startInPersonSigning(document: ApsDealDocument, signature: DealDocumentSignature): void {
    if (!this.canWriteDeals()) return;
    const signerName = signature.signee?.name ?? 'Signer';
    const returnUrl = this.buildSigningReturnUrl();

    this.#dealDocService
      .startDocuSignInPersonSigning(document.id, signature.id, signerName, returnUrl, this.signNowButtonId(document.id))
      .subscribe({
        next: session => {
          window.location.href = session.signingUrl;
        }
      });
  }

  openExecutiveEmailSigning(document: ApsDealDocument, signature: DealDocumentSignature): void {
    if (!this.canWriteDeals()) return;
    const returnUrl = this.buildSigningReturnUrl();

    this.#dealDocService.getDocuSignSignerSigningUrl(document.id, signature.id, returnUrl).subscribe({
      next: session => {
        window.location.href = session.signingUrl;
      }
    });
  }

  openSendToExecutiveConfirmationModal(document: ApsDealDocument): void {
    if (!this.canWriteDeals()) return;
    this.activeDocument.set(document);
    this.#modalService
      .open(this.sendToExecutiveConfirmationModal(), this.#ngbModalService.confirmationModal)
      .result.then(
        () => this.onSendToExecutiveRequested(document.id),
        () => undefined
      );
  }

  openResolveSignerConfirmationModal(document: ApsDealDocument, signature: DealDocumentSignature): void {
    this.activeDocument.set(document);
    this.activeSignature.set(signature);
    this.#modalService.open(this.resolveSignerConfirmationModal(), this.#ngbModalService.confirmationModal).result.then(
      () => this.onResolveSignerRequested(document, signature),
      () => this.activeSignature.set(null)
    );
  }

  onResolveSignerRequested(document: ApsDealDocument, signature: DealDocumentSignature): void {
    this.#salesApsService.waiveSigner(document.id, signature.id).subscribe({
      next: () => {
        const dealId = this.resolveDealId(document.id);
        if (dealId && this.signingContext() === 'misc') {
          this.#dealDocService.applySignerCompleteInCache(
            dealId,
            document.id,
            signature.id,
            SignatureCompletionSource.Waived
          );
        }
        this.activeSignature.set(null);
        this.refreshRequested.emit();
      },
      error: () => this.activeSignature.set(null)
    });
  }

  openRegenerateContractConfirmationModal(document: ApsDealDocument): void {
    if (!this.canWriteDeals()) return;
    this.activeDocument.set(document);
    this.#modalService
      .open(this.regenerateContractConfirmationModal(), { ...this.#ngbModalService.confirmationModal, size: 'lg' })
      .result.then(
        () => this.onRegenerateRequested(document.documentType),
        () => undefined
      );
  }

  openVoidContractReasonModal(document: ApsDealDocument): void {
    if (!this.canWriteDeals()) return;
    this.activeDocument.set(document);
    this.showVoidReasonError.set(false);
    this.#modalService
      .open(this.voidContractReasonModal(), { ...this.#ngbModalService.confirmationModal, size: 'lg' })
      .result.then(
        () => this.showVoidReasonError.set(false),
        () => this.showVoidReasonError.set(false)
      );
  }

  confirmVoidContract(modal: { close: () => void }, comment: string): void {
    if (!this.canWriteDeals()) return;
    const document = this.activeDocument();
    const reason = this.selectedVoidReason();
    if (!document || !reason) {
      this.showVoidReasonError.set(true);
      return;
    }

    const reasonText = reason.requiresComment ? comment.trim() : reason.description;
    if (!reasonText) {
      this.showVoidReasonError.set(true);
      return;
    }

    modal.close();
    this.onVoidRequested(document.id, {
      reasonId: reason.id,
      reasonText,
      comment: comment.trim()
    });
    this.selectedVoidReasonId.set('');
    this.voidComment.set('');
  }

  buildSigningReturnUrl(): string {
    const url = new URL(window.location.href);
    url.searchParams.delete('event');
    url.searchParams.delete('signedDocumentId');
    url.searchParams.delete('signedSignerId');
    return url.toString();
  }

  private handleSigningCompleteReturn(): void {
    const documentId = this.#route.snapshot.queryParamMap.get('signedDocumentId');
    const signerId = this.#route.snapshot.queryParamMap.get('signedSignerId');
    const dealId = this.resolveDealId(documentId);

    if (dealId && documentId && signerId && this.signingContext() === 'misc') {
      this.#dealDocService.refreshDealNonApsDocumentsAfterSigning(dealId, documentId, signerId).subscribe({
        next: () => this.refreshRequested.emit()
      });
    } else {
      this.refreshRequested.emit();
    }

    setTimeout(() => this.refreshRequested.emit(), 20000);
  }

  onVoidRequested(documentId: string, payload: VoidRequestedPayload): void {
    if (!this.canWriteDeals()) return;
    const dealId = this.resolveDealId(documentId);
    if (!dealId) {
      return;
    }

    const buttonId = this.voidContractButtonId(documentId);
    if (this.effectiveOrigin() === 'aps') {
      this.#dealDocService.voidDealContract(dealId, { voidReason: payload.reasonText }, buttonId).subscribe({
        next: () => {
          this.#dealDocService.clearDealDocumentTemplateState(dealId);
          this.refreshRequested.emit();
          this.#salesApsService.realoadDeal.next(null);
          this.voidContractCheck.set(false);
        }
      });
    } else {
      this.#dealDocService.miscDocVoidSign(documentId, payload.reasonText, buttonId).subscribe({
        next: () => {
          this.#dealDocService.removeDealNonApsDocumentFromCache(dealId, documentId);
          this.refreshRequested.emit();
          this.#salesApsService.realoadDeal.next(null);
          this.voidContractCheck.set(false);
        }
      });
    }
  }

  onSendToExecutiveRequested(dealDocumentId: string): void {
    if (!this.canWriteDeals()) return;
    if (!dealDocumentId) {
      return;
    }

    this.#dealDocService.sendToExecutive(dealDocumentId, this.sendToExecutiveButtonId(dealDocumentId)).subscribe({
      next: () => {
        const dealId = this.resolveDealId(dealDocumentId);
        if (dealId && this.signingContext() === 'misc') {
          this.#dealDocService.getDealNonApsDocuments(dealId).subscribe({
            next: () => this.refreshRequested.emit()
          });
        } else {
          this.refreshRequested.emit();
        }
        this.#salesApsService.realoadDeal.next(null);
      }
    });
  }

  onRegenerateRequested(documentType: string): void {
    if (!this.canWriteDeals()) return;
    if (this.effectiveOrigin() === 'misc') {
      const document = this.activeDocument();
      if (!document) {
        return;
      }
      this.#dealDocService.regenerateNonApsContract(document.id, documentType, this.resolveDealId()).subscribe({
        next: () => this.refreshRequested.emit()
      });
    } else {
      this.#dealDocService.regenerateDealContract(this.resolveDealId()).subscribe({
        next: () => {
          this.#dealDocService.clearDealDocumentTemplateState(this.resolveDealId());
          this.refreshRequested.emit();
        }
      });
    }
  }

  onCardRefreshRequested(): void {
    const dealId = this.resolveDealId();
    if (dealId && this.signingContext() === 'misc') {
      this.#dealDocService.getDealNonApsDocuments(dealId).subscribe({
        next: () => this.refreshRequested.emit()
      });
    } else {
      this.refreshRequested.emit();
    }

    this.voidContractCheck.set(true);
    this.#salesApsService.realoadDeal.next(null);
  }

  private resolveDealId(documentId?: string | null): string {
    if (documentId) {
      const matched = this.documents().find(doc => doc.id === documentId);
      if (matched?.dealId) {
        return matched.dealId;
      }
    }

    return this.activeDocument()?.dealId ?? this.documents()[0]?.dealId ?? '';
  }

  private removeSigningReturnQueryParamsFromUrl(): void {
    const tree = this.#router.parseUrl(this.#router.url);
    const { event, signedDocumentId, signedSignerId } = tree.queryParams;

    if (event === undefined && signedDocumentId === undefined && signedSignerId === undefined) {
      return;
    }

    delete tree.queryParams['event'];
    delete tree.queryParams['signedDocumentId'];
    delete tree.queryParams['signedSignerId'];
    this.#router.navigateByUrl(tree, { replaceUrl: true });
  }

  scheduleAutoRefresh(): void {
    this.clearAutoRefreshTimeout();
    this.#refreshTimeoutId = setTimeout(() => {
      if (this.documents().length > 0) {
        this.refreshRequested.emit();
      }
      this.scheduleAutoRefresh();
    }, environment.pageDataRefreshIntervalMs);
  }

  clearAutoRefreshTimeout(): void {
    if (this.#refreshTimeoutId === null) {
      return;
    }
    clearTimeout(this.#refreshTimeoutId);
    this.#refreshTimeoutId = null;
  }
}
